import { useRef, useEffect } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, Link2, Quote, RemoveFormatting,
} from "lucide-react";

interface RichTextEditorProps {
  onChange: (html: string) => void;
  placeholder?: string;
  resetSignal?: number;
}

interface ToolButton {
  icon: typeof Bold;
  title: string;
  run: () => void;
}

export function RichTextEditor({ onChange, placeholder, resetSignal }: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (resetSignal === undefined) return;
    if (ref.current) {
      ref.current.innerHTML = "";
      onChange("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (cmd: string, value?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, value);
    emit();
  };

  const addLink = () => {
    const raw = window.prompt("Link URL (https://…)");
    const url = raw?.trim();
    if (!url) return;
    if (!/^(https?:|mailto:|tel:)/i.test(url)) {
      window.alert("Only http, https, mailto, and tel links are allowed.");
      return;
    }
    exec("createLink", url);
  };

  const buttons: (ToolButton | "sep")[] = [
    { icon: Bold, title: "Bold", run: () => exec("bold") },
    { icon: Italic, title: "Italic", run: () => exec("italic") },
    { icon: UnderlineIcon, title: "Underline", run: () => exec("underline") },
    "sep",
    { icon: Heading1, title: "Heading 1", run: () => exec("formatBlock", "<h1>") },
    { icon: Heading2, title: "Heading 2", run: () => exec("formatBlock", "<h2>") },
    { icon: Heading3, title: "Heading 3", run: () => exec("formatBlock", "<h3>") },
    "sep",
    { icon: List, title: "Bulleted list", run: () => exec("insertUnorderedList") },
    { icon: ListOrdered, title: "Numbered list", run: () => exec("insertOrderedList") },
    { icon: Quote, title: "Quote", run: () => exec("formatBlock", "<blockquote>") },
    "sep",
    { icon: Link2, title: "Insert link", run: addLink },
    { icon: RemoveFormatting, title: "Clear formatting", run: () => { exec("removeFormat"); exec("formatBlock", "<p>"); } },
  ];

  return (
    <div className="rounded-md border bg-background" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b px-1.5 py-1">
        {buttons.map((b, i) =>
          b === "sep" ? (
            <span key={`sep-${i}`} className="mx-0.5 h-5 w-px bg-border" />
          ) : (
            <button
              key={b.title}
              type="button"
              aria-label={b.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={b.run}
              className="a11y-tip inline-flex h-7 w-7 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              data-tip={b.title}
              data-testid={`button-format-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <b.icon className="h-4 w-4" aria-hidden="true" />
            </button>
          ),
        )}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        data-placeholder={placeholder}
        className="prose prose-sm dark:prose-invert max-w-none min-h-[160px] max-h-[360px] overflow-y-auto px-3 py-2 text-sm leading-relaxed focus:outline-none empty:before:pointer-events-none empty:before:text-muted-foreground/60 empty:before:content-[attr(data-placeholder)]"
        data-testid="input-rich-text"
      />
    </div>
  );
}
